import React, { useMemo } from 'react';
import Select from 'react-select';
import { dropdownTemplate } from '../../../theme';

/**
 * Multi-select filters for spread results: ticker, expiration and spread type.
 *
 * Options are derived from the current result set rather than the full preset
 * list, so a filter never offers a value that would empty the table.
 */
function SpreadFilterControls({ results, filters, onChange }) {
  const options = useMemo(() => {
    const tickers = new Set();
    const expirations = new Set();
    const spreadTypes = new Set();
    (results || []).forEach((row) => {
      if (row['Ticker']) tickers.add(row['Ticker']);
      if (row['Expiration']) expirations.add(row['Expiration']);
      if (row['Spread Type']) spreadTypes.add(row['Spread Type']);
    });
    const toOptions = (set, sorter) => Array.from(set).sort(sorter).map((v) => ({ value: v, label: v }));
    return {
      tickers: toOptions(tickers, (a, b) => a.localeCompare(b)),
      expirations: toOptions(expirations, (a, b) => a.localeCompare(b)),
      spreadTypes: toOptions(spreadTypes, (a, b) => a.localeCompare(b)),
    };
  }, [results]);

  const handleChange = (key) => (selected) => {
    onChange({ ...filters, [key]: (selected || []).map((s) => s.value) });
  };

  const valueFor = (key) => (filters?.[key] || []).map((v) => ({ value: v, label: v }));

  const hasFilters = ['tickers', 'expirations', 'spreadTypes']
    .some((k) => (filters?.[k] || []).length > 0);

  if (!results || results.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3 items-end mb-4">
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <label className="block text-xs text-gray-400 mb-1">Filter by Ticker</label>
        <Select
          isMulti
          options={options.tickers}
          value={valueFor('tickers')}
          onChange={handleChange('tickers')}
          placeholder="All tickers"
          styles={dropdownTemplate.reactSelectStyles}
        />
      </div>
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <label className="block text-xs text-gray-400 mb-1">Filter by Expiration</label>
        <Select
          isMulti
          options={options.expirations}
          value={valueFor('expirations')}
          onChange={handleChange('expirations')}
          placeholder="All expirations"
          styles={dropdownTemplate.reactSelectStyles}
        />
      </div>
      <div style={{ minWidth: 180, flex: '1 1 180px' }}>
        <label className="block text-xs text-gray-400 mb-1">Filter by Spread Type</label>
        <Select
          isMulti
          options={options.spreadTypes}
          value={valueFor('spreadTypes')}
          onChange={handleChange('spreadTypes')}
          placeholder="All spread types"
          styles={dropdownTemplate.reactSelectStyles}
        />
      </div>
      {hasFilters && (
        <button
          type="button"
          onClick={() => onChange({ tickers: [], expirations: [], spreadTypes: [] })}
          className="text-sm py-2 px-3 rounded border bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-[#218aff]/10 hover:text-[#218aff] hover:border-[#218aff] transition-all"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

export default SpreadFilterControls;
